import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import Select from '../../components/select'
import Input from '../../components/input'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { ApplicationStatusOptions } from '../../utils/enums'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import './ApplicationStatusUpdateModal.scss'

const ApplicationStatusUpdateModal = ({ isOpen, application, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  const [formData, setFormData] = useState({
    status: '',
    changeReason: '',
    notes: ''
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false)
  const [documentsError, setDocumentsError] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    if (isOpen && application) {
      // Set current status as default
      const currentStatus = application?.applicationStatus || application?.status || ''
      setFormData({
        status: currentStatus,
        changeReason: '',
        notes: ''
      })
    }

    if (!isOpen) {
      setFormData({
        status: '',
        changeReason: '',
        notes: ''
      })
      setValidationErrors({})
      setSelectedFiles([])
      setDocumentsError('')
      setIsUploadingDocuments(false)
    }
  }, [isOpen, application])

  const handleChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.status || formData.status.trim() === '') {
      errors.status = t('APPLICATIONS_STATUS_UPDATE_VALIDATION_STATUS_REQUIRED')
    }

    if (!formData.changeReason || formData.changeReason.trim() === '') {
      errors.changeReason = t('APPLICATIONS_STATUS_UPDATE_VALIDATION_CHANGE_REASON_REQUIRED')
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFileChange = (e) => {
    const incomingFiles = Array.from(e.target.files || [])
    if (!incomingFiles.length) {
      return
    }

    const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified}`
    const existing = selectedFiles
    const merged = [...existing]
    const seen = new Set(existing.map(fileKey))

    incomingFiles.forEach((file) => {
      const key = fileKey(file)
      if (!seen.has(key)) {
        merged.push(file)
        seen.add(key)
      }
    })

    if (merged.length > 10) {
      setDocumentsError(t('APPLICATIONS_STATUS_UPDATE_DOCUMENTS_LIMIT'))
      setSelectedFiles(merged.slice(0, 10))
    } else {
      setDocumentsError('')
      setSelectedFiles(merged)
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveFile = (indexToRemove) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove))
  }

  const uploadStatusDocuments = (filesToUpload) =>
    new Promise((resolve, reject) => {
      if (!filesToUpload.length) {
        resolve(null)
        return
      }

      const formDataUpload = new FormData()
      filesToUpload.forEach((file) => {
        formDataUpload.append('files', file)
      })

      dispatch(
        ApiCallActions.Post({
          url: `files/applications/${application.id}/status-documents`,
          showAlertOnError: false,
          showLoading: false,
          axiosOptions: {
            data: formDataUpload
          },
          onSuccess: ({ data }) => {
            resolve(data?.data || data)
          },
          onError: ({ errorMessage }) => {
            reject(errorMessage || t('APPLICATIONS_STATUS_UPDATE_DOCUMENTS_ERROR'))
          }
        })
      )
    })

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    dispatch(
      ApiCallActions.Put({
        url: `applications/${application.id}/status`,
        data: {
          status: formData.status,
          changeReason: formData.changeReason.trim(),
          notes: formData.notes.trim() || undefined
        },
        showAlertOnError: true,
        showLoading: true,
        onSuccess: async ({ data }) => {
          const updatedData = data?.data || data
          
          EventBus.sc.onEntityUpdated({
            entityType: 'application',
            entityId: application.id,
            entityData: updatedData
          })

          if (selectedFiles.length > 0) {
            setIsUploadingDocuments(true)
            setDocumentsError('')
            try {
              await uploadStatusDocuments(selectedFiles)
              general.messageSuccess(t('APPLICATIONS_STATUS_UPDATE_DOCUMENTS_SUCCESS'))
            } catch (errorMessage) {
              setDocumentsError(errorMessage)
              general.notificationError(errorMessage)
            }
            setIsUploadingDocuments(false)
          }

          general.messageSuccess(t('APPLICATIONS_STATUS_UPDATE_SUCCESS'))
          setIsSubmitting(false)

          if (onSuccess) {
            onSuccess(updatedData)
          }

          onClose()
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('APPLICATIONS_STATUS_UPDATE_ERROR'))
          setIsSubmitting(false)
        }
      })
    )
  }

  const statusOptions = ApplicationStatusOptions.map(opt => ({
    value: opt.value,
    label: t(opt.label)
  }))

  if (!application) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('APPLICATIONS_STATUS_UPDATE_TITLE')}
      subtitle={t('APPLICATIONS_STATUS_UPDATE_SUBTITLE')}
      icon="zmdi-info-outline"
      size="medium"
    >
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-12">
            <div className="form-group">
              <label>{t('APPLICATIONS_STATUS_UPDATE_FIELD_STATUS')}</label>
              <Select
                name="status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                placeholder={t('APPLICATIONS_STATUS_UPDATE_FIELD_STATUS')}
                options={statusOptions}
                required
                icon="zmdi-info"
              />
              {validationErrors.status && (
                <small className="text-danger">{validationErrors.status}</small>
              )}
            </div>
          </div>
        </div>

        <div className="row m-t-15">
          <div className="col-md-12">
            <div className="form-group">
              <label>{t('APPLICATIONS_STATUS_UPDATE_FIELD_CHANGE_REASON')}</label>
              <Input
                type="text"
                name="changeReason"
                value={formData.changeReason}
                onChange={(e) => handleChange('changeReason', e.target.value)}
                placeholder={t('APPLICATIONS_STATUS_UPDATE_FIELD_CHANGE_REASON_PLACEHOLDER')}
                required
                icon="zmdi zmdi-help-outline"
              />
              {validationErrors.changeReason && (
                <small className="text-danger">{validationErrors.changeReason}</small>
              )}
            </div>
          </div>
        </div>

        <div className="row m-t-15">
          <div className="col-md-12">
            <div className="form-group">
              <label>{t('APPLICATIONS_STATUS_UPDATE_FIELD_NOTES')}</label>
              <textarea
                className="form-control"
                name="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder={t('APPLICATIONS_STATUS_UPDATE_FIELD_NOTES_PLACEHOLDER')}
                rows="4"
              />
            </div>
          </div>
        </div>

        <div className="row m-t-15">
          <div className="col-md-12">
            <div className="form-group status-documents">
              <label>{t('APPLICATIONS_STATUS_UPDATE_FIELD_DOCUMENTS')}</label>
              <div className="status-documents__upload">
                <input
                  type="file"
                  className="status-documents__input"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  disabled={isSubmitting || isUploadingDocuments}
                  ref={fileInputRef}
                />
                <div className="status-documents__placeholder">
                  <i className="zmdi zmdi-collection-pdf status-documents__icon"></i>
                  <div className="status-documents__text">
                    {t('APPLICATIONS_STATUS_UPDATE_DOCUMENTS_HELPER')}
                  </div>
                </div>
              </div>
              {documentsError && (
                <small className="text-danger d-block m-t-5">{documentsError}</small>
              )}
              {selectedFiles.length > 0 && (
                <div className="status-documents__list">
                  {selectedFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="status-documents__item">
                      <div className="status-documents__file">
                        <i className="zmdi zmdi-file status-documents__file-icon"></i>
                        <span className="status-documents__file-name">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm status-documents__remove"
                        onClick={() => handleRemoveFile(index)}
                        disabled={isSubmitting || isUploadingDocuments}
                      >
                        <i className="zmdi zmdi-close"></i>
                      
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="row m-t-20">
          <div className="col-12">
            <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                {t('APPLICATIONS_FORM_BUTTON_CANCEL')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || isUploadingDocuments}
              >
                {isSubmitting || isUploadingDocuments ? t('APPLICATIONS_STATUS_UPDATE_BUTTON_UPDATING') : t('APPLICATIONS_STATUS_UPDATE_BUTTON')}
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}

export default ApplicationStatusUpdateModal

